/* eslint-disable no-console */
import Axios, {
 AxiosError,
 AxiosRequestConfig,
 AxiosRequestHeaders,
} from 'axios'
import {
 ACCESS_TOKEN_KEY,
 REFRESH_TOKEN_KEY,
} from 'constants/common'
import {isPublicPath} from 'helpers/authRedirect'
import {refreshTokens} from 'utils/auth/refreshTokens'
import {
 handleAuthFailure,
 SessionExpiredError,
} from 'utils/auth/sessionManager'
import {getCookieValue} from '../helper'

declare module 'axios' {
 export interface AxiosRequestConfig {
  _retry?: boolean
 }
}

const axios = Axios.create({
 baseURL: process.env.REACT_APP_API_DEV,
})

axios.defaults.headers.post['Content-Type'] =
 'application/json'

interface FailedQueueItem {
 resolve: (token: string) => void
 reject: (error: unknown) => void
}

let isRefreshing = false
let failedQueue: FailedQueueItem[] = []

const processQueue = (
 error: unknown,
 token: string | null = null
) => {
 failedQueue.forEach(({resolve, reject}) => {
  if (error) {
   reject(error)
  } else if (token) {
   resolve(token)
  }
 })
 failedQueue = []
}

const throwSessionExpired = (): never => {
 throw new SessionExpiredError()
}

const shouldHandleAuthFailure = (): boolean =>
 typeof window === 'undefined' ||
 !isPublicPath(window.location.pathname)

const triggerAuthFailureIfNeeded = () => {
 if (shouldHandleAuthFailure()) {
  handleAuthFailure()
 }
}

const isRefreshTokenRequest = (url?: string): boolean =>
 !!url?.includes('/v1/refresh-token')

axios.interceptors.request.use(
 async (config) => {
  const copy = {...config}

  if (typeof localStorage !== 'undefined') {
   const accessToken = getCookieValue(ACCESS_TOKEN_KEY)

   if (accessToken && copy.headers) {
    ;(
     copy.headers as AxiosRequestHeaders
    ).Authorization = `Bearer ${accessToken}`
   }
  }
  if (process.env.REACT_APP_X_API_KEY) {
   ;(copy.headers as AxiosRequestHeaders)['X-API-KEY'] =
    process.env.REACT_APP_X_API_KEY
  }
  ;(copy.headers as AxiosRequestHeaders)['X-PLATFORM'] =
   'web'
  ;(copy.headers as AxiosRequestHeaders)[
   'Access-Control-Allow-Origin'
  ] = `*`

  return copy
 },
 async (error) => Promise.reject(error)
)

axios.interceptors.response.use(
 async (response) => {
  if (response.data.error == null) {
   return response
  }
  return Promise.reject(response.data.error)
 },
 async (error: AxiosError) => {
  const originalRequest = error.config as
   | AxiosRequestConfig
   | undefined

  if (!originalRequest) {
   return Promise.reject(error)
  }

  if (isRefreshTokenRequest(originalRequest.url)) {
   return Promise.reject(error)
  }

  const status = error.response?.status
  const isAuthError = status === 401 || status === 403

  if (!isAuthError) {
   return Promise.reject(error)
  }

  if (originalRequest._retry) {
   triggerAuthFailureIfNeeded()
   throwSessionExpired()
  }

  if (isRefreshing) {
   return new Promise<string>((resolve, reject) => {
    failedQueue.push({resolve, reject})
   }).then(async (token) => {
    if (!originalRequest.headers) {
     originalRequest.headers = {}
    }
    ;(
     originalRequest.headers as AxiosRequestHeaders
    ).Authorization = `Bearer ${token}`
    return axios(originalRequest)
   })
  }

  originalRequest._retry = true
  isRefreshing = true

  const refreshToken = getCookieValue(REFRESH_TOKEN_KEY)
  if (!refreshToken) {
   isRefreshing = false
   triggerAuthFailureIfNeeded()
   throwSessionExpired()
  }

  try {
   const result = await refreshTokens(refreshToken)
   if (!result?.accessToken) {
    processQueue(new SessionExpiredError(), null)
    triggerAuthFailureIfNeeded()
    throw new SessionExpiredError()
   }

   const {accessToken} = result
   processQueue(null, accessToken)
   if (!originalRequest.headers) {
    originalRequest.headers = {}
   }
   ;(
    originalRequest.headers as AxiosRequestHeaders
   ).Authorization = `Bearer ${accessToken}`
   return await axios(originalRequest)
  } catch (refreshError) {
   processQueue(refreshError, null)
   triggerAuthFailureIfNeeded()
   throwSessionExpired()
  } finally {
   isRefreshing = false
  }
 }
)

export default axios
